import { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Briefcase, Target, LineChart, Megaphone, Check } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const SOLUTIONS = [
  { icon: Target, title: "Account-Based Lead Generation", desc: "Targeted B2B lead programs that reach in-market buying committees." },
  { icon: Megaphone, title: "Sponsored Content & Advertising", desc: "Thought-leadership placements across the Lead ITES network." },
  { icon: LineChart, title: "Performance Marketing", desc: "Outcome-based campaigns optimised for pipeline, not vanity metrics." },
  { icon: Briefcase, title: "Research Sponsorship", desc: "Co-branded surveys, benchmarks and white papers with your brand." },
];

const SOLUTION_OPTIONS = SOLUTIONS.map((s) => s.title);
const BUDGETS = ["< $10k", "$10k – $50k", "$50k – $100k", "$100k+"];

const empty = { name: "", email: "", company: "", phone: "", solution: "", budget: "", message: "" };

const Solutions = () => {
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.company || !form.solution) {
      toast.error("Please complete name, work email, company and solution.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/quote-requests", form);
      toast.success("Quote request received. Our team will be in touch shortly.");
      setForm(empty);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="solutions-page">
      <section className="relative bg-navy text-white overflow-hidden">
        <div className="absolute inset-0 grain opacity-40" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-20">
          <span className="overline-label text-electric">Business Solutions</span>
          <h1 className="mt-3 font-display text-4xl lg:text-6xl font-extrabold max-w-3xl leading-tight">
            Commercial programs that generate real demand
          </h1>
          <p className="mt-5 text-white/75 max-w-2xl text-lg">
            From lead generation to sponsored research, Lead ITES builds campaigns engineered to
            produce qualified buyer inquiries and measurable pipeline.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <h2 className="font-display text-3xl font-extrabold text-navy">What we deliver</h2>
          <div className="mt-8 grid sm:grid-cols-2 gap-5">
            {SOLUTIONS.map((s, i) => (
              <motion.div key={s.title}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.08 }}
                className="card-lift bg-white border border-border rounded-xl p-6" data-testid={`solution-${i}`}>
                <div className="h-10 w-10 rounded-lg bg-navy flex items-center justify-center">
                  <s.icon className="h-5 w-5 text-electric" />
                </div>
                <h3 className="mt-4 font-display font-bold text-navy">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </motion.div>
            ))}
          </div>
          <ul className="mt-8 space-y-3">
            {["Dedicated campaign strategist", "Transparent reporting & attribution", "Enterprise data compliance"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-navy/80">
                <Check className="h-5 w-5 text-electric" /> {f}
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:sticky lg:top-24 h-fit">
          <div className="rounded-2xl border border-border bg-white p-8 shadow-sm">
            <h2 className="font-display text-2xl font-extrabold text-navy">Request a quote</h2>
            <p className="mt-2 text-sm text-muted-foreground">Tell us about your goals and we'll build a tailored proposal.</p>
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="quote-form">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Full name *</Label><Input value={form.name} onChange={set("name")} data-testid="quote-name" /></div>
                <div className="space-y-1.5"><Label>Company *</Label><Input value={form.company} onChange={set("company")} data-testid="quote-company" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Work email *</Label><Input type="email" value={form.email} onChange={set("email")} data-testid="quote-email" /></div>
                <div className="space-y-1.5"><Label>Phone</Label><Input value={form.phone} onChange={set("phone")} data-testid="quote-phone" /></div>
              </div>
              <div className="space-y-1.5">
                <Label>Solution of interest *</Label>
                <Select value={form.solution} onValueChange={(v) => setForm((f) => ({ ...f, solution: v }))}>
                  <SelectTrigger data-testid="quote-solution"><SelectValue placeholder="Select a solution" /></SelectTrigger>
                  <SelectContent>
                    {SOLUTION_OPTIONS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Estimated budget</Label>
                <Select value={form.budget} onValueChange={(v) => setForm((f) => ({ ...f, budget: v }))}>
                  <SelectTrigger data-testid="quote-budget"><SelectValue placeholder="Select a range" /></SelectTrigger>
                  <SelectContent>
                    {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Project details</Label><Textarea rows={3} value={form.message} onChange={set("message")} data-testid="quote-message" /></div>
              <Button type="submit" disabled={loading} className="btn-electric w-full rounded-full" data-testid="quote-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Request Quote"}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
